import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import App from "./App.jsx";
import { AuthProvider } from "./context/AuthContext.jsx";
import { ThemeProvider } from "./context/ThemeContext.jsx";
import { AnalyticsProvider } from "./context/AnalyticsContext.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";

import "./styles/landing.css";
import "./styles/app.css";

// Warm the TCP/TLS handshake to the project-specific Supabase URL the
// moment the main bundle parses. The actual SDK fetch happens later
// (after auth context mounts), so by then the connection is already
// established and getSession()'s first network call skips ~100-300 ms
// of handshake latency. Hardcoded dns-prefetch entries live in
// index.html for hosts we know at build time.
(function preconnectSupabase() {
  const url =
    import.meta.env.VITE_SUPABASE_URL ||
    import.meta.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!url) return;
  try {
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = url;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  } catch {
    /* document not ready / disallowed, non-fatal */
  }
})();

// Lazy-load Sentry only when configured. Keeps the ~200 KB SDK out of
// the initial bundle entirely in dev and on any deployment without a
// DSN. The plain <ErrorBoundary> still catches render errors and
// forwards them to Sentry once it boots (via window.__lmSentry__).
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  import("./lib/sentry.js")
    .then((m) => m.initSentry?.())
    // eslint-disable-next-line no-console
    .catch((e) => console.warn("[sentry] failed to load:", e));
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <HelmetProvider>
          <ThemeProvider>
            <AuthProvider>
              <AnalyticsProvider>
                <App />
              </AnalyticsProvider>
            </AuthProvider>
          </ThemeProvider>
        </HelmetProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
